"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";

interface Screenshot {
  src: string;
  alt: string;
  caption: string;
}

// Phone screenshots are 1290 × 2796 (App Store 6.9-inch size).
const WIDTH = 1290;
const HEIGHT = 2796;

export default function MobileScreenshots({ screenshots }: { screenshots: readonly Screenshot[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const current = open === null ? null : screenshots[open];
  const step = (by: number) => setOpen((index) => (index === null ? null : (index + by + screenshots.length) % screenshots.length));

  return (
    <>
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {screenshots.map((shot, index) => (
          <li key={shot.src}>
            <button
              type="button"
              onClick={() => setOpen(index)}
              className="group block w-full cursor-zoom-in rounded-[1.4rem] border bg-black p-1.5 text-left shadow-sm transition hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              aria-label={`Enlarge screenshot: ${shot.caption}`}
            >
              <Image
                src={shot.src}
                alt={shot.alt}
                width={WIDTH}
                height={HEIGHT}
                sizes="(min-width: 640px) 25vw, 50vw"
                className="h-auto w-full rounded-[1.1rem] transition group-hover:opacity-90"
              />
            </button>
            <p className="mt-2 text-sm leading-snug text-muted-foreground">{shot.caption}</p>
          </li>
        ))}
      </ul>

      <Dialog open={current !== null} onOpenChange={(value) => !value && setOpen(null)}>
        <DialogContent
          className="max-h-[95dvh] w-[calc(100%_-_2rem)] max-w-md gap-3 overflow-y-auto rounded-lg p-4"
          onKeyDown={(event) => {
            if (event.key === "ArrowRight") step(1);
            if (event.key === "ArrowLeft") step(-1);
          }}
        >
          {current && (
            <>
              <DialogTitle className="pr-8 text-base">Screenshot {open! + 1} of {screenshots.length}</DialogTitle>
              <DialogDescription>{current.caption}</DialogDescription>
              <Image
                src={current.src}
                alt={current.alt}
                width={WIDTH}
                height={HEIGHT}
                sizes="(min-width: 448px) 416px, 100vw"
                className="mx-auto h-auto max-h-[70dvh] w-auto rounded-xl bg-black"
              />
              <div className="flex justify-between">
                <button type="button" onClick={() => step(-1)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm hover:bg-accent">
                  <ChevronLeft size={16} aria-hidden="true" /> Previous
                </button>
                <button type="button" onClick={() => step(1)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm hover:bg-accent">
                  Next <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
